package org.zq.linkgame.ui.components

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsPressedAsState
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import org.zq.linkgame.ui.theme.*

/**
 * Primary game button with orange-red gradient and shadow effect
 * Used for "开始游戏" button
 */
@Composable
fun PrimaryGameButton(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true
) {
    val interactionSource = remember { MutableInteractionSource() }
    val isPressed by interactionSource.collectIsPressedAsState()
    val scale by animateFloatAsState(if (isPressed) 0.95f else 1f)
    
    Box(
        modifier = modifier
            .graphicsLayer {
                scaleX = scale
                scaleY = scale
            }
            .shadow(
                elevation = 5.dp,
                shape = RoundedCornerShape(15.dp),
                ambientColor = OrangeRedDark.copy(alpha = 0.5f),
                spotColor = OrangeRedDark.copy(alpha = 0.5f)
            )
            .clip(RoundedCornerShape(15.dp))
            .background(
                brush = Brush.verticalGradient(
                    colors = listOf(OrangeRedLight, OrangeRed)
                )
            )
            .clickable(
                interactionSource = interactionSource,
                indication = null,
                enabled = enabled,
                onClick = onClick
            )
            .height(90.dp),
        contentAlignment = Alignment.Center
    ) {
        AppText(
            text = text,
            style = MaterialTheme.typography.headlineLarge,
            color = Color.White,
            maxLines = 1,
            textAlign = TextAlign.Center
        )
    }
}

/**
 * Secondary game button with light orange-red color
 * Used for "关卡选择" button
 */
@Composable
fun SecondaryGameButton(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true
) {
    val interactionSource = remember { MutableInteractionSource() }
    val isPressed by interactionSource.collectIsPressedAsState()
    val scale by animateFloatAsState(if (isPressed) 0.95f else 1f)
    
    Box(
        modifier = modifier
            .graphicsLayer {
                scaleX = scale
                scaleY = scale
            }
            .shadow(
                elevation = 3.dp,
                shape = RoundedCornerShape(12.dp)
            )
            .clip(RoundedCornerShape(12.dp))
            .background(LightOrangeRed)
            .clickable(
                interactionSource = interactionSource,
                indication = null,
                enabled = enabled,
                onClick = onClick
            )
            .height(70.dp),
        contentAlignment = Alignment.Center
    ) {
        AppText(
            text = text,
            style = MaterialTheme.typography.headlineMedium,
            color = DarkGray,
            maxLines = 1,
            textAlign = TextAlign.Center
        )
    }
}

/**
 * Tertiary game button with light yellow color
 * Used for "排行榜" button
 */
@Composable
fun TertiaryGameButton(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true
) {
    val interactionSource = remember { MutableInteractionSource() }
    val isPressed by interactionSource.collectIsPressedAsState()
    val scale by animateFloatAsState(if (isPressed) 0.95f else 1f)
    
    Box(
        modifier = modifier
            .graphicsLayer {
                scaleX = scale
                scaleY = scale
            }
            .shadow(
                elevation = 2.dp,
                shape = RoundedCornerShape(10.dp)
            )
            .clip(RoundedCornerShape(10.dp))
            .background(LightYellow)
            .clickable(
                interactionSource = interactionSource,
                indication = null,
                enabled = enabled,
                onClick = onClick
            )
            .height(56.dp),
        contentAlignment = Alignment.Center
    ) {
        AppText(
            text = text,
            style = MaterialTheme.typography.headlineSmall,
            color = DeepBlue,
            maxLines = 1,
            textAlign = TextAlign.Center
        )
    }
}

/**
 * Circular icon button
 * Used for settings button
 */
@Composable
fun CircleIconButton(
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    size: Dp = 60.dp,
    backgroundColor: Color = LightBlue,
    content: @Composable () -> Unit
) {
    val interactionSource = remember { MutableInteractionSource() }
    val isPressed by interactionSource.collectIsPressedAsState()
    val scale by animateFloatAsState(if (isPressed) 0.9f else 1f)
    
    Box(
        modifier = modifier
            .graphicsLayer {
                scaleX = scale
                scaleY = scale
            }
            .size(size)
            .clip(CircleShape)
            .background(backgroundColor)
            .clickable(
                interactionSource = interactionSource,
                indication = null,
                onClick = onClick
            ),
        contentAlignment = Alignment.Center
    ) {
        content()
    }
}

/**
 * Bottom action button
 * Used for "联系我们" and "反馈" buttons
 */
@Composable
fun BottomActionButton(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    isPrimary: Boolean = true
) {
    val interactionSource = remember { MutableInteractionSource() }
    val isPressed by interactionSource.collectIsPressedAsState()
    val scale by animateFloatAsState(if (isPressed) 0.95f else 1f)
    
    Box(
        modifier = modifier
            .graphicsLayer {
                scaleX = scale
                scaleY = scale
            }
            .width(150.dp)
            .height(45.dp)
            .clip(RoundedCornerShape(8.dp))
            .background(if (isPrimary) DeepBlue else LightBlue)
            .clickable(
                interactionSource = interactionSource,
                indication = null,
                onClick = onClick
            ),
        contentAlignment = Alignment.Center
    ) {
        AppText(
            text = text,
            style = MaterialTheme.typography.titleSmall,
            color = if (isPrimary) Color.White else DeepBlue,
            maxLines = 1,
            textAlign = TextAlign.Center
        )
    }
}

/**
 * Category tab button
 * Used in level selection screen
 */
@Composable
fun CategoryTabButton(
    text: String,
    isSelected: Boolean,
    onClick: () -> Unit,
    textColor: Color = DeepGreen,
    modifier: Modifier = Modifier
) {
    val interactionSource = remember { MutableInteractionSource() }
    
    Column(
        modifier = modifier
            .clickable(
                interactionSource = interactionSource,
                indication = null,
                onClick = onClick
            ),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        AppText(
            text = text,
            style = MaterialTheme.typography.bodyMedium,
            color = if (isSelected) textColor else textColor.copy(alpha = 0.6f)
        )
        if (isSelected) {
            Spacer(modifier = Modifier.height(4.dp))
            Box(
                modifier = Modifier
                    .width(50.dp)
                    .height(3.dp)
                    .background(textColor, RoundedCornerShape(1.5.dp))
            )
        } else {
            Spacer(modifier = Modifier.height(7.dp))
        }
    }
}

/**
 * Triangle navigation button
 * Used for back button and page navigation
 */
@Composable
fun TriangleNavigationButton(
    onClick: () -> Unit,
    isLeft: Boolean = true,
    modifier: Modifier = Modifier,
    size: Dp = 50.dp,
    backgroundColor: Color = DeepGreen
) {
    val interactionSource = remember { MutableInteractionSource() }
    val isPressed by interactionSource.collectIsPressedAsState()
    val scale by animateFloatAsState(if (isPressed) 0.9f else 1f)
    
    Box(
        modifier = modifier
            .graphicsLayer {
                scaleX = scale
                scaleY = scale
                rotationZ = if (isLeft) 0f else 180f
            }
            .size(size)
            .clickable(
                interactionSource = interactionSource,
                indication = null,
                onClick = onClick
            ),
        contentAlignment = Alignment.Center
    ) {
        TriangleIcon(
            size = size,
            color = backgroundColor
        )
    }
}
